let TagalysCustomisations = {}

/**
 * Utility function to filter out temp-unavailable products
 * @param {Array} products - Array of products to filter
 * @returns {Array} - Filtered products without temp-unavailable tag
 */
function filterAvailableProducts(products) {
  return products.filter(item => Array.isArray(item.tags) && !item.tags.includes('temp-unavailable'));
}

/**
 * Shared product card render function.
 * @param {Function} html - Renders HTML strings.
 * @param {Object} args - Tagalys arguments.
 * @param {String} placementType - Placement type, 'search-results' or
 * 'predictive-search'.
 * @returns {String}
 */
function renderProduct(html, args, placementType) {
  const props = args.props;
  const product = props.product;
  const helpers = props.helpers;

  const productOptions = helpers.getTemplateOptions('product');
  const productLink = productOptions.productPageUrl(product);

  let activeLabel = '';

  const productGroupReferences = product.metafields?.custom?.product_group?.reference?.fields.find((field) => {
    return field.key === 'products'
  })?.references?.edges || [];

  const productSizeGuideFields = product.metafields?.custom?.size_guide?.reference?.fields;
  const productSizeGuideData = {};

  productSizeGuideFields?.forEach(field => {
    if (field.key === 'image') {
      productSizeGuideData.image = field.reference?.image?.url || '';
    } else if (field.key === 'pdf') {
      productSizeGuideData.pdf = field.reference?.url || '';
    } else {
      productSizeGuideData[field.key] = field.value;
    }
  });

  let productSizeGuideButton = null;

  if (productSizeGuideFields && productSizeGuideFields.length > 0) {
    const productSizeGuideRawData = JSON.stringify(productSizeGuideData);

    productSizeGuideButton = html`
      <size-guide class="size-guide-wrapper">
        <button
          class="size-guide-button"
          type="button"
          data-size-guide='${productSizeGuideRawData}'
        >
          ${window.tagalysConfiguration.strings.sizeGuide}
        </button>
      </size-guide>
    `;
  }

  const productGroupMaterialReferences = productGroupReferences?.map((edge) => {
    const productItem = edge.node;

    if (productItem.metafield === null) {
      return '';
    }

    const materialMetafield = productItem.metafield?.reference?.fields;
    let materialColor = materialMetafield[0].value;
    let materialLabel = materialMetafield[1].value;
    let active = false;

    if (productItem.handle == product.handle) {
      active = true;
      activeLabel = materialLabel;
    }

    return html`
      <button
        class="
          product-card-swatches__swatch
          button-reset
          ${active ? 'is-active' : ''}
        "
        style="background-color: ${materialColor};"
        title=""
        type="button"
        data-handle="${productItem.handle}"
        js-product-card="swatch"
      >
        <span class="visually-hidden">${materialLabel}</span>
      </button>
    `;
  });

  let hasAnyActiveVariant = false;

  for (let variant of product.variants) {
    if (variant.available === true) {
      hasAnyActiveVariant = true;
      break;
    }
  }

  const variantButtons = product.variants.map((variant, index) => {
    return html `
      <button
        class="
          variant-button
          quick-add-to-cart__button
          quick-add-modal__button
          quick-add-modal__button--${index}
        "
        ${!variant.available ? 'disabled' : ''}
        type="button"
        data-variant-id="${variant.id}"
      >
        ${variant.title}

        <span class="quick-add-modal__loading-spinner hidden">
          <${args.templates.loadingIcon}/>
        </span>
      </button>
    `
  });

  const generateProductBadgeHTML = (product, config) => {
    const settings = config.productBadges;

    let badgesDisplay = '';
    let badgeCount = 0;
    let badgesDisplayArray = [];

    if (!product.available) {
      const soldOutText = config.strings.soldOut;
      badgesDisplay = html`<span class="so_badge_color card-product__badge caption-m"><${args.templates.badgeIcon}/>${soldOutText}</span>`;

    } else {
      const priorityBadges = [];
      const otherBadges = [];

      if (product.compare_at_price > product.price ) {
        const onSaleText = config.strings.onSale;

        badgesDisplay = html`<span class="sale_badge_color card-product__badge caption-m"><${args.templates.badgeIcon}/>${onSaleText}</span>`;
        badgeCount++;
      }

      product.tags.forEach((tag) => {
        if (tag.includes('badge-1:')) {
          priorityBadges.push(tag);
        } else if (tag.includes('badge-')) {
          otherBadges.push(tag);
        }
      });

      const allBadges = [...priorityBadges, ...otherBadges];

      allBadges.forEach((badge) => {
        if (badgeCount < settings.badgeCount && badge) {
          const badgeText = badge.split(':').pop().trim();
          const badgeTextHandle = badgeText.toLowerCase().replace(/ /g, '-');
          const badgeStyle = `color:var(--color-badge-text-${badgeTextHandle}) !important;`;

          badgesDisplayArray.push(html`<span class="special_badge_color card-product__badge caption-m" style="${badgeStyle}"><${args.templates.badgeIcon}/>${badgeText}</span>`);

          badgeCount++;
        }
      });
    }

    return html`
      ${badgesDisplay || badgesDisplayArray.length > 0 ? html`
        <div class="card__badge ${settings.badgePosition}">
          ${badgesDisplay ? html `
            ${badgesDisplay}
          ` : ''}
          ${badgesDisplayArray.length > 0 ? html`
            ${badgesDisplayArray}
          ` : ''}
        </div>
      ` : ''}
    `;
  }

  const badgeHTML = generateProductBadgeHTML(product, window.tagalysConfiguration);

  // Featured Image
  const image = product.featured_image;
  const widths = [165, 360, 533, 720, 940, 1066];
  const sizes = `(min-width: ${window.tagalysConfiguration.pageWidth}px) ${Math.floor((window.tagalysConfiguration.pageWidth - 130) / 4)}px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)`;
  let featuredImage = '';

  if (image) {
    const srcset = widths
      .filter(w => image.width >= w)
      .map(w => `${image.src}?width=${w} ${w}w`)
      .join(', ') + `, ${image.src} ${image.width}w`;

    featuredImage = html`
      <img
        class="motion-reduce"
        alt=${product.title}
        height="${image.height}"
        loading="${props.index > 3 ? 'lazy' : 'eager'}"
        sizes="${sizes}"
        src="${image.src}?width=533"
        srcset="${srcset}"
        width="${image.width}"
      />
    `
  }

  // Secondary Image
  const imageSecondary = product.images[1];
  const showSecondaryImage = window.tagalysConfiguration.showSecondaryImage;
  let secondaryImage = '';

  if (imageSecondary && showSecondaryImage) {
    const srcsetSecondary = widths
      .filter(w => imageSecondary.width >= w)
      .map(w => `${imageSecondary.src}?width=${w} ${w}w`)
      .join(', ') + `, ${imageSecondary.src} ${imageSecondary.width}w`;

    secondaryImage = html`
      <img
        class="motion-reduce"
        alt=${product.title}
        height="${imageSecondary.height}"
        loading="lazy"
        sizes="${sizes}"
        src="${imageSecondary.src}?width=533"
        srcset="${srcsetSecondary}"
        width="${imageSecondary.width}"
      />
    `;
  }

  const productCardClass = placementType === 'search-results'
    ? 'grid__item main-collection-product-grid__item'
    : 'grid__item slider__slide scroll-trigger animate--slide-in'

  return html`
    <li
      id="Slide-${placementType}-${product.id}"
      class="${productCardClass}"
      data-id=${product.id}
    >
      <product-card>
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div
            class="card card--standard ${product.featured_image ? 'card--media' : 'card--text'}"
            style="--ratio-percent: 100%;"
          >
            <div
              class="card__inner card__inner-hover color-scheme-2 gradient ratio"
              style="--ratio-percent: 100%;"
            >
              <div class="card-product__quick-ctas">
                ${!(product.has_only_default_variant || product.variants.length <= 1) ? (html`
                  <button
                    class="card-product__quick-add-button"
                    type="button"
                    aria-label="Quick add to cart"
                    ${!hasAnyActiveVariant ? '' : 'disabled'}
                  >
                    <${args.templates.addToBagIcon}/>
                  </button>
                `) : (html`
                  <product-form data-section-id="${placementType}">
                    <form
                      id="quick-add-form-${product.id}"
                      class="form"
                      method="post"
                      action="/cart/add"
                      accept-charset="UTF-8"
                      enctype="multipart/form-data"
                      novalidate="novalidate"
                      data-type="add-to-cart-form"
                    >
                      <input type="hidden" name="form_type" value="product" />
                      <input type="hidden" name="utf8" value="✓" />

                      <input
                        class="product-variant-id"
                        name="id"
                        type="hidden"
                        value="${product.variants[0].id}"
                        ${product.variants[0].available == true ? '' : 'disabled'}
                      />

                      <input type="hidden" name="quantity" value="1" />

                      <button
                        id="${product.id}-submit"
                        type="submit"
                        name="add"
                        class="quick-add__submit"
                        aria-haspopup="dialog"
                        aria-labelledby="${product.id}-submit title-${product.id}"
                        aria-live="polite"
                        data-sold-out-message="true"
                        ${product.variants[0].available == true ? '' : 'disabled'}
                      >
                        <${args.templates.addToBagIcon} />

                        <span class="sold-out-message hidden">
                          ${window.tagalysConfiguration.strings.soldOut}
                        </span>

                        <span class="quick-add-modal__loading-spinner loading__spinner hidden">
                          <${args.templates.loadingIcon} />
                        </span>
                      </button>

                      <input type="hidden" name="product-id" value="${product.id}" />
                      <input type="hidden" name="section-id" value="${placementType}" />
                    </form>
                  </product-form>
                `)}
              </div>

              ${product.featured_media && product.featured_media.media_type === 'video' ? (
                html`
                  <a
                    href=${productLink}
                    id="StandardCardNoMediaLink-${product.id}"
                    class="full-unstyled-link card-product__imag-link"
                    aria-labelledby="StandardCardNoMediaLink-${product.id} NoMediaStandardBadge-${product.id}"
                  >
                    <div class="card__media">
                      <div class="media media--transparent media--hover-effect">
                        <video-container>
                          <video
                            src="${product.featured_media.src}"
                            autoplay="${window.tagalysConfiguration.videoControls.videoAutoplay}"
                            loop="${window.tagalysConfiguration.videoControls.videoLoop}"
                            muted
                            playsinline
                            width="100%"
                            height="100%"
                          ></video>

                          ${window.tagalysConfiguration.videoControls.showVideoControls ? html`
                            <div class="video-controls-banner">
                              <div class="video-controller">
                                ${window.tagalysConfiguration.videoControls.showVideoMuteControls ? html`
                                  <${args.templates.videoSoundController}/>
                                ` : null}
                                <${args.templates.videoPlayPauseController}/>
                              </div>
                            </div>
                          ` : null}
                        </video-container>
                      </div>
                    </div>
                  </a>
                `
              ) : (
                // fallback to image rendering (your existing code)
                html `
                <a
                  id="StandardCardNoMediaLink-${product.id}"
                  class="full-unstyled-link card-product__imag-link"
                  href=${productLink}
                  aria-labelledby="StandardCardNoMediaLink-${product.id} NoMediaStandardBadge-${product.id}"
                >
                  <div class="card__media">
                    <div class="media media--transparent media--hover-effect">
                      ${featuredImage ? featuredImage : null}
                      ${secondaryImage ? secondaryImage : null}
                    </div>
                  </div>
                </a>
                `
              )}

              <div class="card__content">
                <div class="card__information">
                  <h3
                    class="card__heading body-2"
                    ${product.featured_media && window.tagalysConfiguration.cartStyle == 'standard' ? html`
                      id="title-${product.id}"
                    `: null}
                  >
                    ${product.title}
                  </h3>
                </div>

                ${badgeHTML}
              </div>

              ${!product.has_only_default_variant ?
                product.variants.length > 0 ? (html`
                  <drawer-bottom class="product-card__variants">
                    <quick-add-to-cart-modal class="quick-add-modal__container">
                      <div class="quick-add-modal__header">
                        <div class="quick-add-modal__horizontal-bar"></div>

                        <p class="quick-add-modal__subtitle body-2 mobile">
                          ${window.tagalysConfiguration.strings.mobileSizeGuideTitle}
                        </p>
                      </div>

                      <product-form
                        class="product-form"
                        data-section-id="predictive-search"
                      >
                        <form
                          id="quick-add-form-${product.id}"
                          class="form"
                          accept-charset="UTF-8"
                          action="/cart/add"
                          data-type="add-to-cart-form"
                          enctype="multipart/form-data"
                          method="post"
                          novalidate="novalidate"
                        >
                          <input type="hidden" name="form_type" value="product" />
                          <input type="hidden" name="utf8" value="✓" />

                          <div
                            class="product-form__error-message-wrapper"
                            hidden
                            role="alert"
                          >
                            <span class="svg-wrapper">
                              <${args.templates.errorIcon}/>
                            </span>

                            <span class="product-form__error-message"></span>
                          </div>

                          <input
                            class="product-variant-id"
                            name="id"
                            type="hidden"
                            value="${product.variants[0].id}"
                          />

                          <input type="hidden" name="quantity" value="1" />

                          <div class="quick-add-to-cart-modal__buttons">
                            ${variantButtons}
                          </div>

                          <button type="submit" name="add" class="hidden">
                            <span>${window.tagalysConfiguration.strings.addToCart}</span>

                            <span class="loading__spinner hidden">
                              <${args.templates.loadingIcon}/>
                            </span>
                          </button>

                          <input type="hidden" name="product-id" value="${product.id}" />
                          <input type="hidden" name="section-id" value="predictive-search" />
                        </form>
                      </product-form>

                      ${productSizeGuideButton}
                    </quick-add-to-cart-modal>
                  </drawer-bottom>
                `) : null
              : null}
            </div>

            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading body-2">
                  <a
                    class="full-unstyled-link"
                    href=${productLink}
                  >
                    ${product.title}
                  </a>
                </h3>

                <div class="card-information">
                ${helpers.isStorefrontAPICallPending() ? (
                  html `<span class="skeleton-box"></span>`
                ) : (
                  html `<div
                    class=${`price ${
                      helpers.isProductOnSale(
                        product.compare_at_price,
                        product.price
                      )
                        ? " price--on-sale"
                        : " "
                    }`}
                  >
                    <div class="price__container body-1 mobile">
                      <span class="price-item price-item--regular">
                        ${helpers.formatCurrency(
                          product.compare_at_price === null || product.compare_at_price === 0
                            ? product.price
                            : product.compare_at_price
                        )}
                      </span>
                      ${helpers.isProductOnSale(
                        product.compare_at_price,
                        product.price
                      )
                        ? html`
                            <span class="price-item price-item--sale price-item--last"
                              >${product.variants.length > 0 ? window.tagalysConfiguration.productCard.fromPrice :""} ${helpers.formatCurrency(product.price)}</span
                            >
                          `
                        : null}
                    </div>
                  </div>`
                )}
                </div>
                ${activeLabel ? html`
                  <div class="product-card-swatches">
                    <div class="product-card-swatches__swatches">
                      ${productGroupMaterialReferences.map(materialReference => {
                        return html`${materialReference}`;
                      })}
                    </div>
                    ${window.tagalysConfiguration.showColourSwatchLabel ? html`
                      <span
                        class="product-card-swatches__label caption-m"
                        js-product-card="swatch-label"
                      >
                        ${activeLabel}
                      </span>
                    ` : null}
                  </div>
                `: null}
              </div>
            </div>
          </div>
        </div>
      </product-card>
    </li>
  `;
}

/**
 * Shared render icon functions.
 * @param {Function} html - Renders HTML strings.
 * @param {String} icon - Icon name.
 * @returns {String}
 */
function renderIcon(html, icon) {
  switch (icon) {
    case 'addToBag':
      return html `
        <span class="svg-wrapper-quickadd">
          <svg width="25" height="25" viewBox="0 0 25 25" class="icon icon-cart" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_11987_722)"><rect x="4.70508" y="6.56396" width="15.5" height="13.5" stroke="white" stroke-width="1.5" /><path d="M7.87609 10.539C8.03919 10.539 8.20028 10.4818 8.32292 10.3732C8.44645 10.2637 8.522 10.109 8.522 9.94105L8.522 5.74386C8.522 4.83586 8.92889 3.95864 9.66411 3.30725C10.4002 2.65508 11.4038 2.28481 12.4552 2.28481C13.5065 2.28481 14.5101 2.65508 15.2462 3.30725C15.9815 3.95864 16.3884 4.83586 16.3884 5.74386L16.3884 9.94105C16.3884 10.109 16.4639 10.2637 16.5874 10.3732C16.7101 10.4818 16.8712 10.539 17.0343 10.539C17.1974 10.539 17.3585 10.4818 17.4811 10.3732C17.6046 10.2637 17.6802 10.109 17.6802 9.94105L17.6802 5.74386C17.6802 4.49991 17.1222 3.31332 16.1399 2.44305C15.1585 1.57356 13.8327 1.08897 12.4552 1.08897C11.0776 1.08897 9.75182 1.57356 8.77043 2.44305C7.78816 3.31332 7.23017 4.49991 7.23017 5.74386L7.23017 9.94105C7.23018 10.109 7.30573 10.2637 7.42925 10.3732C7.55189 10.4818 7.71298 10.539 7.87609 10.539Z" fill="white" stroke="white" stroke-width="0.45" /></g><defs><clipPath id="clip0_11987_722"><rect width="24" height="24" fill="white" transform="translate(0.272461 0.313965)" /></clipPath></defs></svg>
        </span>
      `;

    case 'badge':
      return html `
        <svg width="6" height="7" viewBox="0 0 6 7" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2.82812" y="0.642578" width="4" height="4" transform="rotate(45 2.82812 0.642578)" fill="#2D231D"/></svg>
      `;

    case 'closeSecondary':
      return html `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" class="icon icon-close" xmlns="http://www.w3.org/2000/svg"><path d="M6 18L18 6M6 6L18 18" stroke="#191919" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
      `;

    case 'closeTertiary':
      return html `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close-small" viewBox="0 0 12 13"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"></path></svg>
      `;

    case 'downArrow':
      return html`
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.64645 8.64645C5.84171 8.45118 6.15829 8.45118 6.35355 8.64645L12 14.2929L17.6464 8.64645C17.8417 8.45118 18.1583 8.45118 18.3536 8.64645C18.5488 8.84171 18.5488 9.15829 18.3536 9.35355L12.3536 15.3536C12.1583 15.5488 11.8417 15.5488 11.6464 15.3536L5.64645 9.35355C5.45118 9.15829 5.45118 8.84171 5.64645 8.64645Z" fill="#191919"/></svg>
      `;

    case 'error':
      return html `
        <svg class="icon icon-error" viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="2"/><circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"/><path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"/><path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"/></svg>
      `;

    case 'leftArrow':
      return html `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.1382 18.1382C15.2002 18.0762 15.2495 18.0027 15.2831 17.9217C15.3167 17.8407 15.334 17.7539 15.334 17.6662C15.334 17.5786 15.3167 17.4918 15.2831 17.4108C15.2495 17.3298 15.2002 17.2562 15.1382 17.1943L10.2763 12.3338L15.1382 7.4733C15.2633 7.34814 15.3336 7.17838 15.3336 7.00138C15.3336 6.82438 15.2633 6.65462 15.1382 6.52946C15.013 6.4043 14.8432 6.33398 14.6662 6.33398C14.4892 6.33398 14.3195 6.4043 14.1943 6.52946L8.86189 11.8619C8.79982 11.9238 8.75057 11.9974 8.71696 12.0783C8.68336 12.1593 8.66606 12.2461 8.66606 12.3338C8.66606 12.4215 8.68336 12.5083 8.71696 12.5893C8.75057 12.6703 8.79982 12.7438 8.86189 12.8057L14.1943 18.1382C14.2562 18.2002 14.3298 18.2495 14.4108 18.2831C14.4918 18.3167 14.5786 18.334 14.6662 18.334C14.7539 18.334 14.8407 18.3167 14.9217 18.2831C15.0027 18.2495 15.0762 18.2002 15.1382 18.1382Z" fill="#191919"></path></svg>
      `;

    case 'loading':
      return html `
        <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>
      `;

    case 'rightArrow':
      return html`
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.86184 5.86184C8.79977 5.92376 8.75052 5.99731 8.71691 6.07829C8.68331 6.15927 8.66602 6.24609 8.66602 6.33376C8.66602 6.42144 8.68331 6.50825 8.71691 6.58923C8.75052 6.67021 8.79977 6.74376 8.86184 6.80568L13.7237 11.6662L8.86184 16.5267C8.73668 16.6519 8.66637 16.8216 8.66637 16.9986C8.66637 17.1756 8.73668 17.3454 8.86184 17.4705C8.987 17.5957 9.15676 17.666 9.33376 17.666C9.51077 17.666 9.68052 17.5957 9.80568 17.4705L15.1381 12.1381C15.2002 12.0762 15.2494 12.0026 15.283 11.9217C15.3166 11.8407 15.3339 11.7539 15.3339 11.6662C15.3339 11.5785 15.3166 11.4917 15.283 11.4107C15.2494 11.3297 15.2002 11.2562 15.1381 11.1943L9.80568 5.86184C9.74376 5.79977 9.67021 5.75052 9.58923 5.71692C9.50825 5.68331 9.42144 5.66602 9.33376 5.66602C9.24609 5.66602 9.15927 5.68331 9.07829 5.71692C8.99731 5.75052 8.92376 5.79977 8.86184 5.86184Z" fill="#191919"></path></svg>
      `;

    case 'videoSoundController':
      return html `
        <div class="video-sound-control">
          <span class="mute-button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#ffffff" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.6775 4.54935C11.8508 4.63261 11.9609 4.8078 11.9609 5V19C11.9609 19.1922 11.8508 19.3674 11.6775 19.4507C11.5043 19.5339 11.2987 19.5105 11.1486 19.3904L6.28555 15.5H2.46094C2.1848 15.5 1.96094 15.2761 1.96094 15V9C1.96094 8.72386 2.1848 8.5 2.46094 8.5H6.28555L11.1486 4.60957C11.2987 4.4895 11.5043 4.46609 11.6775 4.54935ZM10.9609 6.04032L6.77328 9.39044C6.68463 9.46136 6.57447 9.5 6.46094 9.5H2.96094V14.5H6.46094C6.57447 14.5 6.68463 14.5386 6.77328 14.6096L10.9609 17.9597V6.04032Z" fill="#ffffff"/><path fill-rule="evenodd" clip-rule="evenodd" d="M23.8145 8.64645C24.0098 8.84171 24.0098 9.15829 23.8145 9.35355L17.8145 15.3536C17.6192 15.5488 17.3026 15.5488 17.1074 15.3536C16.9121 15.1583 16.9121 14.8417 17.1074 14.6464L23.1074 8.64645C23.3026 8.45118 23.6192 8.45118 23.8145 8.64645Z" fill="#ffffff"/><path fill-rule="evenodd" clip-rule="evenodd" d="M17.1074 8.64645C17.3026 8.45118 17.6192 8.45118 17.8145 8.64645L23.8145 14.6464C24.0098 14.8417 24.0098 15.1583 23.8145 15.3536C23.6192 15.5488 23.3026 15.5488 23.1074 15.3536L17.1074 9.35355C16.9121 9.15829 16.9121 8.84171 17.1074 8.64645Z" fill="fffffff"/></svg>
          </span>
          <span class="sound-button display-none">
            <svg width="21" height="16" viewBox="0 0 21 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.46094 1L4.46094 5H0.460938V11H4.46094L9.46094 15V1Z" fill="#ffffff"/><path d="M17.531 0.929993C19.4057 2.80527 20.4588 5.34835 20.4588 7.99999C20.4588 10.6516 19.4057 13.1947 17.531 15.07M14.001 4.45999C14.9383 5.39763 15.4649 6.66917 15.4649 7.99499C15.4649 9.32081 14.9383 10.5924 14.001 11.53" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
        </div>
      `;

    case 'videoPlayPauseController':
      return html `
        <div class="video-play-pause-control">
          <span class="play-button">
            <svg width="25" height="24" viewBox="0 0 25 24" fill="#ffffff" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.26139 4.1599C6.39012 3.69167 5.32422 4.30785 5.32422 5.27975V18.72C5.32422 19.7205 6.44694 20.3329 7.31795 19.8077L19.1033 12.701C19.9557 12.1869 19.9244 10.9652 19.0467 10.4935L7.26139 4.1599Z" fill="#ffffff"/></svg>
          </span>
          <span class="pause-button display-none">
            <svg width="49" height="50" viewBox="0 0 49 50" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="-0.5" width="48" height="48" rx="24" transform="matrix(1 0 0 -1 0 48.8477)" stroke="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M20 31.3477L20 19.3477L22 19.3477L22 31.3477L20 31.3477Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M27 31.3477L27 19.3477L29 19.3477L29 31.3477L27 31.3477Z" fill="white"/></svg>
          </span>
        </div>
      `;
  }
}

/**
 * Handle successful API call.
 * @param {Object} response - Tagalys response.
 * @param {String} placementType - Placement type, 'search-results' or
 * 'predictive-search'.
 * @returns {Object}
 */
async function handleSuccessfulAPICall(response, placementType) {
  const responseProducts = placementType === 'search-results'
    ? filterAvailableProducts(response.products || [])
    : response.sections?.products.items;

  const productIds = responseProducts?.map((item) => item.id);

  /**
   * No products to fetch metafields for.
   */
  if (!productIds || productIds.length === 0) {
    return response;
  }

  const accessToken = window.tagalysConfiguration.storeFrontAPIToken;
  const myShopifyDomain = Shopify.shop;
  const apiVersion = window.tagalysConfiguration.storeFrontAPIVersion;

  /**
   * Load additional metafield data.
   */
  const productDetailsToMetafields = await fetchMetafieldDetailsForProducts(productIds, accessToken, myShopifyDomain, apiVersion);

  response.products = responseProducts?.map((product) => {
    const metafieldsDetails = productDetailsToMetafields[product.id];

    /**
     * No metafield found, return product as is.
     */
    if (!metafieldsDetails) {
      return product;
    }

    /**
     * Add featured media to product.
     */
    if (metafieldsDetails.featuredMedia) {
      const mediaNode = metafieldsDetails.featuredMedia;

      product.featured_media = {
        id: mediaNode.id,
        media_type: mediaNode.mediaContentType?.toLowerCase().replace('_', '') || 'image',
        src: mediaNode.sources?.[0]?.url || mediaNode.embedUrl || mediaNode.image?.url || ''
      };
    }

    for (const [key, metafieldDetails] of Object.entries(metafieldsDetails)) {
      if (!metafieldDetails || !metafieldDetails.namespace || !metafieldDetails.key) {
        continue;
      }

      product.metafields[metafieldDetails.namespace] ||= {};
      product.metafields[metafieldDetails.namespace][metafieldDetails.key] = metafieldDetails
    }

    return product;
  });

  return response;
}

/**
 * Fetch metafield details for products using Shopify Storefront API.
 * @param {Array} productIds - Array of product IDs.
 * @param {String} storefrontAccessToken - Storefront API access token.
 * @param {String} shopDomain - Shopify shop domain.
 * @param {String} apiVersion - Shopify API version.
 * @returns {Promise<Object>} - A mapping of product IDs to their metafield details.
 */
async function fetchMetafieldDetailsForProducts(productIds, storefrontAccessToken, shopDomain, apiVersion) {
  const endpoint = `https://${shopDomain}/api/${apiVersion}/graphql.json`;

  // Build the GraphQL query to get products and their metaobjects
  const query = `
    query getProductsWithMetaobjects($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          handle
          media(first: 1) {
            edges {
              node {
                ... on Video {
                  id
                  mediaContentType
                  sources {
                    url
                    mimeType
                    format
                    height
                    width
                  }
                }
                ... on ExternalVideo {
                  id
                  mediaContentType
                  embedUrl
                }
                ... on MediaImage {
                  id
                  mediaContentType
                  image {
                    url
                    altText
                    width
                    height
                  }
                }
                ... on Model3d {
                  id
                  mediaContentType
                  sources {
                    url
                    mimeType
                    format
                  }
                }
              }
            }
          }
          productGroup: metafield(namespace: "custom", key: "product_group") {
            namespace
            key
            reference {
              ... on Metaobject{
                fields{
                  key
                  value
                  references(first: 50) {
                    edges {
                      node {
                        ... on Product{
                          id
                          handle
                          metafield(namespace: "custom", key: "material") {
                            namespace
                            key
                            value
                            reference {
                              ... on Metaobject {
                                fields {
                                  key
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          material: metafield(namespace: "custom", key: "material") {
            namespace
            key
            reference {
              ... on Metaobject {
                fields {
                  key
                  value
                }
              }
            }
          },
          sizeGuide: metafield(namespace: "custom", key: "size_guide") {
            namespace
            key
            reference {
              ... on Metaobject {
                fields {
                  key
                  value
                  reference {
                    ... on MediaImage {
                      image {
                        url
                      }
                    }
                    ... on GenericFile {
                      url
                    }
                  }
                }
              }
            }
          },
        }
      }
    }
  `;

  // Convert product IDs to Shopify GID format if they aren't already
  const formattedIds = productIds.map(id => {
    return `gid://shopify/Product/${id}`;
  });

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      body: JSON.stringify({
        query,
        variables: {
          ids: formattedIds
        }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    // Process and return the results
    let productDetailsToMetafields = {}

    data.data.nodes.forEach(product => {
      if (product === null) return;

      const productId = product.id.replace('gid://shopify/Product/', '');

      // Extract the first media item as featuredMedia
      const featuredMedia = product.media?.edges?.[0]?.node || null;

      productDetailsToMetafields[productId] = {
        productGroup: product.productGroup,
        material: product.material,
        sizeGuide: product.sizeGuide,
        featuredMedia: featuredMedia
      };

    });

    return productDetailsToMetafields;

  } catch (error) {
    console.error('Error fetching product group metaobjects:', error);
    throw error;
  }
}

/**
 * Open and close the quick add modal for mobile devices.
 */
function openQuickAddModalMobile() {
  document.addEventListener('modal:opened', () => {
    const overlaySearchModals = document.querySelectorAll('[js-tagalys="drawer-overlay-search"]');
    document.querySelector('[js-background-overlay="drawer"]').style.display = "none";

    overlaySearchModals.forEach((overlaySearchModal) => {
      overlaySearchModal.classList.add('open');
    })
  });
}

/**
 * Close the quick add modal for mobile devices.
 */
function closeQuickAddModalMobile() {
  document.addEventListener('modal:closed', () => {
    const overlaySearchModals = document.querySelectorAll('[js-tagalys="drawer-overlay-search"]');

    overlaySearchModals.forEach((overlaySearchModal) => {
      overlaySearchModal.classList.remove('open');
    })

    document.querySelector('[js-background-overlay="drawer"]').style.display = "flex";
  });
}

/**
 * Decode HTML entities in a string.
 * @param {String} str
 * @returns {String}
 */
function decodeHtmlEntities(str) {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

/**
 * Check if the current device is a desktop.
 * @returns {Boolean}
 */
function isDesktop() {
  return window.innerWidth >= 750;
}

/**
 * Queries cart data and then loads recommendations.
 * - If smart recommendations then no need to load cart data.
 * @param {Boolean} [hideSection] - Hide section if no products.
 */
function queryCartAndLoadRecommendations(hideSection) {
  if (window.tagalysConfiguration.recommendationsType === 'smart') {
    TagalysCustomisations.initRecommendations();
    return;
  }

  fetch(`${window.routes.cart_url}.js`)
    .then((response) => response.json())
    .then((cart) => {
      if (!cart.item_count) {
        if (hideSection && window.templateData.name === 'cart') {
          const tagalysSection = document.querySelector('[js-cart-recommendations="section"]');

          if (tagalysSection) {
            tagalysSection.classList.remove('is-active');
          }
        }

        return;
      }

      /**
       * Limit of three product IDs.
       */
      const productIds = cart.items?.map((item) => item.product_id).slice(0, 3);

      /**
       * Initialise recommendations.
       */
      TagalysCustomisations.initRecommendations(productIds);
    })
    .catch((error) => {
      console.error('Tagalys recommendations cart load error', error);
    });
}

/**
 * Tagalys search suggestions init.
 */
TagalysCustomisations.initSearchSuggestions = function() {
  const searchElementsFormHeader = '[js-tagalys="search-input-header"]';
  const searchElementsFormMain = '[js-tagalys="search-input-main"]';
  const searchButtonHeader = '[js-tagalys="search-button-header"]';

  if (window.templateData.name === 'search') {
    Tagalys.UIWidgets.SearchSuggestions.init(searchElementsFormMain, {
      searchResultsURL: window.tagalysConfiguration.searchUrl,
      templates: {
        widget: {
          options: {
            sections: {
              products: {
                include: ["total_count", "items"],
                count: 5
              },
              collections: {
                count: 5
              },
              pages: {
                count: 5
              }
            },
            selector: '[js-tagalys="search-suggestions-results-page"]',
            position: false
          }
        }
      }
    });
  }

  Tagalys.UIWidgets.SearchSuggestions.init(searchElementsFormHeader, {
    searchResultsURL: window.tagalysConfiguration.searchUrl,
    callbacks: {
      onSuccessfulAPICall: (response) => handleSuccessfulAPICall(response, 'predictive-search'),
    },
    templates: {
      widget: {
        options: {
          sections: {
            products: {
              include: ["total_count", "items"],
              count: 5
            },
            collections: {
              count: 5
            },
            pages: {
              count: 5
            }
          },
          selector: '[js-tagalys="search-suggestions"]',
          position: false,
          minimumCharactersToShowSuggestions: 2
        },
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers;

          return html`
            <div
              ref=${props.tagalysSearchSuggestionsPopoverRef}
              class="search-suggestions search-results-tagalys exclude-tagalys-css-reset"
            >
                <div class="drawer-overlay-search" js-tagalys="drawer-overlay-search"></div>
                ${helpers.exceedsMinimumCharactersToShowSuggestions()
                ? html`
                  <${args.templates.typeSuggestions} />
                  <${args.templates.hint} />
                `
                : html` <${args.templates.clickSuggestions} /> `}
            </div>
          `;
        },
        callbacks: {
          afterInitialRender: function(helpers) {
            document.querySelector(searchElementsFormHeader).addEventListener('input', (event) => {
              if (helpers.exceedsMinimumCharactersToShowSuggestions()) {
                document.querySelector(searchButtonHeader).removeAttribute('disabled');
              }
              else {
                document.querySelector(searchButtonHeader).setAttribute('disabled', 'disabled');
              }
            });
          }
        }
      },
      clickSuggestions: {
        render: function (html, args) {
          const helpers = args.props.helpers
          const recentAndPopularSearches = helpers.getRecentAndPopularSearches()

          return html`
            <ul class="click-suggestions exclude-tagalys-css-reset">
              ${recentAndPopularSearches.recentSearches.map(
                recentSearch =>
                  html`
                    <${args.templates.recentSearchItem} recentSearch=${recentSearch} />
                  `
            )}
              ${recentAndPopularSearches.popularSearches.map(
                popularSearch =>
                  html`
                    <${args.templates.popularSearchItem} popularSearch=${popularSearch} />
                  `
              )}
            </ul>
          `;
        }
      },
      recentSearchItem: {
        render: function (html, args) {
          const props = args.props
          const helpers = props.helpers
          const enteredSearchQuery = helpers.getEnteredSearchQuery()
          return html`
            <li class="click-suggestion recent-search-item">
              <a
                class="search-results-link"
                href=${helpers.getLink(props.recentSearch.query)}
              >
                <${args.templates.recentSearchIcon} />
                <${args.templates.highlighter}
                  label=${props.recentSearch.query}
                  highlight=${enteredSearchQuery}
                />
              </a>
            </li>
          `;
        }
      },
      typeSuggestions: {
        render: function (html, args) {
          const props = args.props
          const helpers = props.helpers
          const textSuggestions = helpers.getTextSuggestions()

          let classes = ["type-suggestions", "exclude-tagalys-css-reset" ]
          if (helpers.hasTextSearchSuggestions() > 0) {
            classes.push("product-and-text-suggestions")
          } else {
            classes.push("product-suggestions")
          }
          let className = classes.join(" ")

          return html`
            <div class=${className}>

              ${helpers.hasTextSearchSuggestions() ?
                html`
                  <ul class="text-suggestions exclude-tagalys-css-reset">
                    ${textSuggestions.map(
                      textSuggestionSection =>
                        html`
                          <${args.templates.textSuggestionSection} textSuggestionSection=${textSuggestionSection} />
                        `
                    )}
                  </ul>
                `: null
              }
              <${args.templates.products}
                class="product-suggestions"
              />
            </div>
        `;
        }
      },
      textSuggestionSection: {
        render: function (html, args) {
          const props = args.props;
          const textSuggestionSection = props.textSuggestionSection;

          return textSuggestionSection.items.length === 0 ? null : html`
            <li
              class="text-suggestion-section"
              data-section-id=${textSuggestionSection.id}
            >
              <span class="section-title">
                ${textSuggestionSection.title}
              </span>
              <ul>
                ${textSuggestionSection.items.map(
                  function (textSuggestionSectionItem) {
                    return html`
                      <${args.templates.textSuggestionSectionItem}
                        textSuggestionSectionId=${textSuggestionSection.id}
                        textSuggestionSectionItem=${textSuggestionSectionItem}
                      />
                    `;
                  }
                )}
              </ul>
            </li>
          `;
        },
      },
      products: {
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers;
          const products = filterAvailableProducts(helpers.getProducts());
          const query = helpers.getEnteredSearchQuery();

          publish(PUB_SUB_EVENTS.search, query);

          return html`
            <div class="search-suggestions-products">
              ${products.length === 0 ? null : html`
                <div class="section-title text-body caption-with-letter-spacing caption-m2">
                  <span>${window.tagalysConfiguration.strings.products}</span>
                  <a class="link-secondary"
                    href=${helpers.getURLEncodedQueryString({ query })}
                  >${window.tagalysConfiguration.strings.viewAll}</a>
                </div>`
              }
              <slider-component
                class="
                  slider-mobile-gutter
                  slider--desktop
                  predictive-search__products-slider
                  search-products-slider
                  exclude-tagalys-css-reset
                "
              >
                <ul
                  id="Slider-predictive-search-products"
                  class="
                    slider slider--tablet grid grid--peek
                    slider--mobile slider--tablet
                    predictive-search__products-slider-ul
                    exclude-tagalys-css-reset
                  "
                >
                ${products.map((product, index) => {
                  return html`
                    <${args.templates.product} key=${product.id} product=${product} />
                  `;
                })}
                </ul>
              </slider-component>
            </div>
          `;
        },
      },
      product: {
        render: (html, args) => renderProduct(html, args, 'predictive-search'),
      },
      hint: {
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers;
          const widgetOptions = helpers.getTemplateOptions("widget")

          const getLabel = () => {
            var labels = args.labels;

            if (helpers.exceedsMinimumCharactersToShowSuggestions()) {
              return window.tagalysConfiguration.strings.searchFor.replace(
                "{{ terms }}",
                helpers.getEnteredSearchQuery()
              )
            }
            return labels.hint.underMinimumCharacters.replace(
              "{{minimumCharactersToShowSuggestions}}",
              widgetOptions.minimumCharactersToShowSuggestions
            )
          }

          return html`
            <div class="hint">
              <a
                class="search-results-link link link--text animate-arrow body-1"
                href=${helpers.getURLEncodedQueryString({
                  query: helpers.getEnteredSearchQuery()
                })}
                >
                <span class="search-results-link__label">${getLabel()}</span>
                <span class="svg-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" viewBox="0 0 14 10"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/></svg>
                </span>
              </a>
            </div>
          `;
        },
      },
      addToBagIcon: {
        render: (html) => renderIcon(html, 'addToBag'),
      },
      badgeIcon: {
        render: (html) => renderIcon(html, 'badge'),
      },
      loadingIcon: {
        render: (html) => renderIcon(html, 'loading'),
      },
      videoSoundController: {
        render: (html) => renderIcon(html, 'videoSoundController'),
      },
      videoPlayPauseController: {
        render: (html) => renderIcon(html, 'videoPlayPauseController'),
      },
      errorIcon: {
        render: (html) => renderIcon(html, 'error'),
      },
    },
  });
};

/**
 * Tagalys search results init.
 */
TagalysCustomisations.initSearchResults = function() {
  Tagalys.UIWidgets.SearchResults.init('[js-tagalys="search-results-container"]', {
    callbacks: {
      onSuccessfulAPICall: (response, helpers) => {
        const templateSearchHeaderH1 = document.querySelector('[js-tagalys="search-header"]');
        const topSearchInputs = document.querySelectorAll('[js-tagalys="search-input-main"]');
        let spanCounter = null;

        if (templateSearchHeaderH1 && helpers.getTotalProductsCount() == 0) {
          spanCounter = document.createElement('span');
          spanCounter.className = 'template-search__counter';
          spanCounter.prepend('('+helpers.getTotalProductsCount()+')');

          templateSearchHeaderH1.prepend(spanCounter);
        }

        if (typeof helpers.getQuery() !== 'undefined') {
          if (topSearchInputs) {
            topSearchInputs.forEach(topSearchInput => {
              topSearchInput.value = helpers.getQuery();
            });
          }
        }

        return handleSuccessfulAPICall(response, 'search-results');
      },
    },
    templates: {
      widget: {
        labels: {
          searchResults: {
            heading: {
              standard: decodeHtmlEntities(window.tagalysConfiguration.strings.searchResults.heading.standard),
              corrected: decodeHtmlEntities(window.tagalysConfiguration.strings.searchResults.heading.corrected),
              partialMatches: decodeHtmlEntities(window.tagalysConfiguration.strings.searchResults.heading.partialMatches),
            },
            itemsCount: window.tagalysConfiguration.strings.searchResults.itemsCount,
            productsCount: window.tagalysConfiguration.strings.searchResults.productsCount,
            noResultsHeading: decodeHtmlEntities(window.tagalysConfiguration.strings.searchResults.noResultsHeading),
          },
          filters: {
            heading: window.tagalysConfiguration.strings.filters.heading,
            clearAllFilters: window.tagalysConfiguration.strings.filters.clearAllFilters,
            clearIndividualFilter: window.tagalysConfiguration.strings.filters.clearIndividualFilter,
            drawerFilterToggle: window.tagalysConfiguration.strings.filters.drawerFilterToggle,
            filterBy: window.tagalysConfiguration.strings.filters.filterBy,
            rangeFilterValueSeparator: window.tagalysConfiguration.strings.filters.rangeFilterValueSeparator,
          },
          sortOptions: {
            sortBy: window.tagalysConfiguration.strings.sortOptions.sortBy,
          },
          pagination: {
            firstPage: window.tagalysConfiguration.strings.pagination.firstPage,
            previousPage: window.tagalysConfiguration.strings.pagination.previousPage,
            nextPage: window.tagalysConfiguration.strings.pagination.nextPage,
            loadNext: window.tagalysConfiguration.strings.pagination.loadNext,
            loadPrevious: window.tagalysConfiguration.strings.pagination.loadPrevious,
            loading: window.tagalysConfiguration.strings.pagination.loading,
          },
        },
        options: {
          canAppendQueryStringToUrl: true,
          limit: 16
        },
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers;

          if (helpers.isInitialLoading()) {
            return html` <${args.templates.loader} /> `;
          }

          let classes = ['plp', 'search-results', 'search-results-tagalys', 'container-max-width', 'exclude-tagalys-css-reset'];
          if (helpers.isFilterDrawerOpen()) {
            classes.push("filter-drawer-opened");
          }
          if (helpers.isLoading()) {
            classes.push("loading");
          }
          if (helpers.canShowFiltersInDrawer()) {
            classes.push("show-filters-in-drawer");
          }

          let className = classes.join(" ");

          if (helpers.hasNoSearchResults() && !helpers.hasAnyFiltersApplied()) {
            return html`
              <div class="plp search-results search-results-tagalys container-max-width exclude-tagalys-css-reset">
                <${args.templates.noResults} />
              </div>
            `;
          }

          return html`
            <div class=${className}>
              <${args.templates.header} />
              ${helpers.isFilterDrawerOpen() ? html` <div class="filter-overlay" onclick=${helpers.closeFilterDrawer}/>` : null}
              <div class="filters-and-products-list page-width">
                <div class="products-list">
                  <${args.templates.productListHeader} />
                  <${args.templates.products} />
                  <${args.templates.productListFooter} />
                </div>
              </div>
              <${args.templates.footer} />
            </div>
          `;
        },
      },
      productListHeader: {
        render: function (html, args) {
          const props = args.props;
          return html`
            <div class="products-list-header">
              <button onClick=${props.helpers.closeFilterDrawer} class="mobile-facets__close"
                aria-label=${window.tagalysConfiguration.strings.close}>
                <span class="svg-wrapper">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.00104 7.29393L0.707107 0L0 0.707107L7.29393 8.00104L2.32458e-05 15.295L0.70713 16.0021L8.00104 8.70815L15.2028 15.9099L15.9099 15.2028L8.70815 8.00104L15.9099 0.799265L15.2028 0.0921578L8.00104 7.29393Z" fill="white"></path></svg>
                </span>
              </button>

              <${args.templates.filters} />

              <${args.templates.infoAndActions} />
              ${props.helpers.canLoadPreviousResults()
                ? html`
                    <${args.templates.loadPrevious} />
                  `
                : null}
            </div>
          `;
        },
      },
      products: {
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers;
          const products = helpers.getProducts();

          return html`
            <ul class="collection-product-grid grid-col-4 mobile-grid--2">
              ${products.map((product, index) => html`
                <${args.templates.product} key=${product.id} product=${product} index=${index}/>
              `)}
            </ul>
          `;
        },
      },
      product: {
        render: (html, args) => renderProduct(html, args, 'search-results')
      },
      noResults: {
        render: function (html, args) {
          const labels = args.labels
          const helpers = args.props.helpers;

          const getHeadingBasedOnAPIResponse = () => {
            const products = filterAvailableProducts(helpers.getProducts());
            const filteredCount = products.length;
            return labels.searchResults.noResultsHeading
                    .replace("{{ results_count }}", filteredCount)
                    .replace("{{ search_term }}", helpers.getQuery());
          };

          return html`
            <div class="no-results">
              <div class="no-results-heading main-search__results-count">
                <p class="h4 exclude-tagalys-css-reset">${getHeadingBasedOnAPIResponse()}</p>
              </div>
              <${args.templates.noResultsWidgets} />
            </div>
          `;
        }
      },
      heading: {
        render: function (html, args) {
          const props = args.props;
          const labels = args.labels;
          const helpers = props.helpers;

          const getHeadingBasedOnAPIResponse = () => {
            const products = filterAvailableProducts(helpers.getProducts());
            const filteredCount = products.length;
            if (helpers.isPartialResults()) {
              return labels.searchResults.heading.partialMatches.replace(
                "{{ search_term }}",
                helpers.getQuery()
              );
            }

            if (helpers.isSpellingCorrectedQuery()) {
              return labels.searchResults.heading.corrected
                .replace("{{ original_search_term }}", helpers.getOriginalQuery())
                .replace("{{ results_count }}", filteredCount)
                .replace("{{ corrected_search_term }}", helpers.getQuery());
            }

            return labels.searchResults.heading.standard
              .replace("{{ results_count }}", filteredCount)
              .replace("{{ search_term }}", helpers.getQuery());
          };
          return html`
            <div class="main-search__results-count exclude-tagalys-css-reset">
              <p class="h4 exclude-tagalys-css-reset">${getHeadingBasedOnAPIResponse()}</p>
            </div>
          `;
        },
      },
      filters: {
        options: {
          collapsible: true,
          includeSortOptionsInFilters: false,
          autoCollapse: true,
          showAppliedFilters: true,
          showClearAllFilters: true,
          showClearActionForIndividualFilters: true,
          showMatchingProductsCount: true,
          defaultExpandedFilters: [],
          showFiltersInDrawer: () => {
            return isDesktop() ? false : true;
          },
          expandTopNFiltersByDefault: false
        },
        render: function (html, args) {
          const props = args.props;
          const labels = args.labels;
          const helpers = props.helpers;

          const filters = helpers.getFilters();
          const appliedFilters = helpers.getAppliedFilters(true);
          const showClearAllFiltersAction =
            props.showClearAllFilters && appliedFilters.length;
          let classes = ["filters"];
          if (props.collapsible) {
            classes.push("collapsible");
          }

          let className = classes.join(" ");
          return html`
            <div class=${className}>
              <${args.templates.filtersHeader} />

              <div class="filter-items">
                ${filters.map(
                  (filter) => html`<${args.templates.filter} filter=${filter} />`
                )}

                <div class="mobile-facets__footer">
                  <button
                    type="button"
                    onClick=${props.helpers.closeFilterDrawer}
                    class="btn primary"
                    aria-label=${window.tagalysConfiguration.strings.showResults}>
                    ${window.tagalysConfiguration.strings.showResults}
                  </button>
                </div>
              </div>
            </div>
            <div class="filters-active active-facets">
              <div class="active-facets__button-wrapper">
                ${showClearAllFiltersAction
                  ? html`
                      <button
                        type="button"
                        class="clear-all-filters active-facets__button-remove underlined-link"
                        onclick=${helpers.clearAllFilters}
                      >
                        ${labels.filters.clearAllFilters}
                      </button>
                    `
                  : null}
                </div>
              ${props.showAppliedFilters
                ? html` <${args.templates.appliedFilters} /> `
                : null}
            </div>
          `;
        },
        callbacks: {
          afterInitialRender: function(helpers) {
            // Add event listener to close expanded filters when clicking outside
            const filterItems = document.querySelectorAll('[js-tagalys="filter-item"]');

            if (isDesktop()) {
              document.addEventListener('click', function (event) {
                if (event.target.closest('.filter') !== null) {
                  return; // Ignore clicks inside filter items
                }

                filterItems.forEach( (filterItem, index) => {
                  if (filterItem &&
                    !filterItem.contains(event.target) &&
                    filterItem.classList.contains('expanded')
                  ) {
                    setTimeout(() => {
                      document.querySelector('[data-filter-id="'+filterItem.dataset.filterId+'"] [js-tagalys="close-filter"]')?.click();
                    }, index * 10); // Delay to ensure the click event is processed in order
                  }
                });
              });
            }

            // Close expanded filters on load
            filterItems.forEach( (filterItem, index) => {
              if (filterItem.classList.contains('expanded')) {
                setTimeout(() => {
                  document.querySelector('[data-filter-id="'+filterItem.dataset.filterId+'"] [js-tagalys="close-filter"]')?.click();
                }, index * 10); // Delay to ensure the click event is processed in order
              }
            });
          }
        }
      },
      filtersHeader: {
        render: function (html, args) {
          const props = args.props;
          const labels = args.labels;
          const helpers = props.helpers;
          const getResultsLabel = () => {
            return labels.searchResults.productsCount.replace("{{ products_count }}", helpers.getTotalProductsCount())
          }
          return html`
            <div class="mobile-facets__header">
              <div class="mobile-facets__header-inner">
                <div class="mobile-facets__header-inner">
                  <h2 class="mobile-facets__heading medium-hide large-up-hide">${labels.filters.filterBy}</h2>
                  <p class="mobile-facets__count medium-hide large-up-hide">
                    ${getResultsLabel()}
                  </p>
                </div>
              </div>
            </div>
          `;
        }
      },
      filter: {
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers
          let classes = ["filter"]
          let isCollapseOpen = false

          if (props.collapsible) {
            isCollapseOpen = helpers.isCollapsibleFilterExpanded(props.filter.id);
            isCollapseOpen ? classes.push("expanded") : classes.push("collapsed");
          }

          let className = classes.join(" ")
          return html`
            <div class=${className}
              data-filter-id=${props.filter.id}
              js-tagalys="filter-item"
            >
              <${args.templates.filterHeader}
                filterHeading=${props.filter.label}
                filterId=${props.filter.id}
              />
              <${args.templates.filterItems}
                filter=${props.filter}
              />
            </div>
          `;
        }
      },
      filterHeader: {
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers
          const filterOptions = helpers.getTemplateOptions("filters")
          const toggleCollapsibleFilter = () => {
            helpers.toggleCollapsibleFilter(props.filterId);
          };

          return html`
            <div
              onClick=${filterOptions.collapsible ? toggleCollapsibleFilter : undefined}
              class="filter-header"
            >
              <h3 class="filter-heading">${props.filterHeading}</h3>
              <div class="filter-actions">
                <span class="filter-action-toggle-collapse">
                  ${filterOptions.collapsible
                    ? html`
                        <${args.templates.downArrow} />
                      `
                    : null}
                </span>
                <span class="mobile-facets__arrow">
                  ${filterOptions.collapsible
                    ? html`
                        <${args.templates.rightArrow} />
                      `
                    : null}
                </span>
              </div>
            </div>
          `;
        }
      },
      filterItems: {
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers
          const labels = args.labels
          const filterOptions = helpers.getTemplateOptions("filters")
          const clearFilter = (event) => {
            event.stopPropagation();
            helpers.clearFilter(props.filter.id);
          };
          const toggleCollapsibleFilter = () => {
            helpers.toggleCollapsibleFilter(props.filter.id);
          };

          const toggleCollapsibleFilterAndFiltersDrawer = () => {
            helpers.toggleCollapsibleFilter(props.filter.id);
            helpers.closeFilterDrawer()
          };

          if (props.filter.type === "checkbox") {
            return html`
              <div class="filter-body filter-body-checkboxes">
                <button type="button" class="mobile-facets__close-button link link--text focus-inset"
                  onClick=${filterOptions.collapsible ? toggleCollapsibleFilter : undefined}>
                  <${args.templates.leftArrow}/>
                  <span class="body-1">${props.filter.label}</span>
                </button>
                <${args.templates.filterCheckBoxes}
                  filterId=${props.filter.id}
                  filterItems=${props.filter.items}
                />
                <div class="facets__header">
                  <button
                    type="button"
                    onClick=${clearFilter}
                    class="facets__reset link underlined-link filter-action-clear">
                    ${labels.filters.clearIndividualFilter}
                  </button>

                  <button
                    aria-label=${window.tagalysConfiguration.strings.close}
                    type="button"
                    class="desktop-facets__close"
                    js-tagalys="close-filter"
                    onClick=${filterOptions.collapsible ? toggleCollapsibleFilter : undefined}
                    >
                    <span class="svg-wrapper">
                      <${args.templates.closeSecondaryIcon} />
                    </span>
                  </button>

                  <button
                    type="button"
                    class="btn primary mobile-filter-facets__close"
                    onclick=${filterOptions.collapsible ? toggleCollapsibleFilterAndFiltersDrawer : undefined}>
                    ${window.tagalysConfiguration.strings.apply}
                  </button>
                </div>
              </div>
            `;
          }
          return html`
            <div class="filter-body filter-body-range">
              <${args.templates.rangeSliderFilterItem} filter=${props.filter} />
            </div>
          `
        }
      },
      appliedFilters: {
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers;

          const appliedFilters = helpers.getAppliedFilters(true);


          if (!appliedFilters.length) return null;
          return html`
            ${appliedFilters.map((appliedFilter) => {
              if (appliedFilter.type === "range") {
                return html`
                  <div
                    onClick=${() => helpers.clearFilter(appliedFilter.id)}
                    class="applied-filter"
                  >
                    <span class="applied-filter-label">
                      ${helpers.labelFormatter(
                        appliedFilter.display_format,
                        appliedFilter.selected_min,
                        appliedFilter.currency
                      )}
                      -
                      ${helpers.labelFormatter(
                        appliedFilter.display_format,
                        appliedFilter.selected_max,
                        appliedFilter.currency
                      )}
                    </span>
                    <span class="applied-filter-action-clear">
                      <${args.templates.closeIcon} />
                    </span>
                  </div>
                `;
              }
              return appliedFilter.items.map((filterItem) => {
                return html`
                  <button
                    type="button"
                    class="active-facets__button active-facets__button--light"
                    onClick=${() =>
                      helpers.clearFilter(appliedFilter.id, filterItem.id)}
                  >
                    <span class="active-facets__button-inner button button--tertiary">
                      <span class="svg-wrapper">
                        <${args.templates.closeTertiaryIcon} />
                      </span>
                      ${appliedFilter.label}: ${filterItem.label}
                      <span class="visually-hidden">
                        ${window.tagalysConfiguration.strings.clearFilter}
                      </span>
                    </span>
                  </button>
                `;
              });
            })}
          `;
        },
      },
      infoAndActions: {
        render: function (html, args) {
          const props = args.props
          const helpers = props.helpers
          const labels = args.labels
          const filterOptions = helpers.getTemplateOptions('filters')
          const products = filterAvailableProducts(helpers.getProducts());
          const filteredCount = products.length;
          const getResultsLabel = () => {
            return labels.searchResults.itemsCount.replace("{{ products_count }}", filteredCount)
          }

          return html`
            <div class="plp-info-and-actions">
              <div class="plp-info-and-actions-toogle">
                <button
                  type="button"
                  onclick=${helpers.openFilterDrawer}
                  class="filter-drawer-toggle mobile-facets__open"
                >
                  <span class="mobile-facets__open-label button-label medium-hide large-up-hide btn-s">
                    ${labels.filters.drawerFilterToggle}
                  </span>
                  <span class="svg-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-filter" viewBox="0 0 20 20"><path fill="currentColor" fill-rule="evenodd" d="M4.833 6.5a1.667 1.667 0 1 1 3.334 0 1.667 1.667 0 0 1-3.334 0M4.05 7H2.5a.5.5 0 0 1 0-1h1.55a2.5 2.5 0 0 1 4.9 0h8.55a.5.5 0 0 1 0 1H8.95a2.5 2.5 0 0 1-4.9 0m11.117 6.5a1.667 1.667 0 1 0-3.334 0 1.667 1.667 0 0 0 3.334 0M13.5 11a2.5 2.5 0 0 1 2.45 2h1.55a.5.5 0 0 1 0 1h-1.55a2.5 2.5 0 0 1-4.9 0H2.5a.5.5 0 0 1 0-1h8.55a2.5 2.5 0 0 1 2.45-2"></path></svg>
                  </span>
                </button>
              </div>
              <div class="results-count-wrapper">
                <strong class="results-count">${getResultsLabel()}</strong>
              </div>
              ${filterOptions.includeSortOptionsInFilters ? null : html` <${args.templates.sortOptions} /> `}
            </div>
          `;
        },
      },
      filterCheckBoxes: {
        render: function (html, args) {
          const props = args.props;
          return html`
            <ul class="filter-checkboxes facets-layout facets-layout-list facets-layout-list--swatch facets__list list-unstyled">
              ${props.filterItems.map(filterItem => {
                return html`
                  <${args.templates.checkboxFilterItem}
                    filterId=${props.filterId}
                    filterItem=${filterItem}
                  >
                  ${filterItem.selected && filterItem.items
                    ? html`
                        <${args.templates.filterCheckBoxes}
                          filterId=${props.filterId}
                          filterItems=${filterItem.items}
                        />
                      `
                    : null
                  }
                </${args.templates.checkboxFilterItem}>
              `;
              })}
            </ul>
          `;
        }
      },
      checkboxFilterItem: {
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers;

          const onFilterClick = (event) => {
            event.stopPropagation();
            if (props.filterItem.selected) {
              helpers.clearFilter(props.filterId, props.filterItem.id);
            } else {
              helpers.applyFilter(props.filterId, props.filterItem.id);
            }
          };

          const filterItemId = props.filterItem.id;
          const filterItemIdParts = filterItemId.split('/');
          const filterItemIdPartsLastPart = filterItemIdParts[filterItemIdParts.length - 1];

          return html`
            <li
              class="filter-item list-menu__item facets__item"
              data-filter-item-id=${props.filterItem.id}
              onClick=${onFilterClick}
            >
              <div class="filter-checkbox-and-label facets__label facet-checkbox">
                <div class="swatch-input-wrapper custom-filter__swatch-input-wrapper">
                  <input
                    class="checkbox swatch-input__input"
                    checked=${props.filterItem.selected}
                    type="checkbox"
                    id="filter-item-${props.filterId}-${filterItemIdPartsLastPart}"
                  />
                  <label class="swatch-input__label"
                    for="filter-item-${props.filterId}-${filterItemIdPartsLastPart}"
                    aria-label="${props.filterItem.label}"
                  >
                    <span class="swatch">
                    </span>
                  </label>
                </div>
                <span class="facet-checkbox__text body-1">
                  <span class="facet-checkbox__text-label">
                    ${props.filterItem.label}
                  </span>
                  ${props.showMatchingProductsCount
                    ? html`
                        <span>(${props.filterItem.count})</span>
                      `
                    : null}
                </div>
              </span>
              ${props.children}
            </li>
          `;
        },
      },
      sortOptions: {
        render: function (html, args) {
          const props = args.props;
          const labels = args.labels;
          const helpers = props.helpers;
          const sortOptions = helpers.getSortOptions();
          const appliedSortOption = helpers.getAppliedSortOption();

          const applySortOption = (event) => {
            helpers.applySortOption(event.target.value);
          };

          return html`
            <div class="sort-options-container facet-filters__field">
              <span class="sort-by-label">
                ${labels.sortOptions.sortBy}
              </span>
              <div class="select">
                <select
                  id="sort-by-options"
                  class="sort-options facet-filters__sort select__select caption-large body-1"
                  value=${appliedSortOption.id}
                  onchange=${applySortOption}
                >
                  ${sortOptions.map((sortOption) => {
                    return html`
                      <option value=${sortOption.id}>${sortOption.label}</option>
                    `;
                  })}
                </select>
                <span class="svg-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.64645 8.64645C5.84171 8.45118 6.15829 8.45118 6.35355 8.64645L12 14.2929L17.6464 8.64645C17.8417 8.45118 18.1583 8.45118 18.3536 8.64645C18.5488 8.84171 18.5488 9.15829 18.3536 9.35355L12.3536 15.3536C12.1583 15.5488 11.8417 15.5488 11.6464 15.3536L5.64645 9.35355C5.45118 9.15829 5.45118 8.84171 5.64645 8.64645Z" fill="#191919"></path></svg>
                </span>
              </div>
            </div>
          `;
        },
      },
      downArrow: {
        render: (html) => renderIcon(html, 'downArrow'),
      },
      rightArrow: {
        render: (html) => renderIcon(html, 'rightArrow'),
      },
      leftArrow: {
        render: (html) => renderIcon(html, 'leftArrow'),
      },
      addToBagIcon: {
        render: (html) => renderIcon(html, 'addToBag'),
      },
      badgeIcon: {
        render: (html) => renderIcon(html, 'badge'),
      },
      loadingIcon: {
        render: (html) => renderIcon(html, 'loading'),
      },
      videoSoundController: {
        render: (html) => renderIcon(html, 'videoSoundController'),
      },
      videoPlayPauseController: {
        render: (html) => renderIcon(html, 'videoPlayPauseController'),
      },
      errorIcon: {
        render: (html) => renderIcon(html, 'error'),
      },
      closeSecondaryIcon: {
        render: (html) => renderIcon(html, 'closeSecondary'),
      },
      closeTertiaryIcon: {
        render: (html) => renderIcon(html, 'closeTertiary'),
      },
      paginationLinks: {
        options: {
          showNumberedPageLinks: true,
          numberedPageLinksWindowSize: 3,
          showPreviousPageLink: true,
          showFirstPageLink: false,
          showNextPageLink: true,
          scrollTop: true
        },
        render: function (html, args) {
          const props = args.props;
          const helpers = props.helpers;
          const labels = args.labels;
          const pagesToDisplay = helpers.getPagesToDisplay(
            props.numberedPageLinksWindowSize
          );
          const currentPage = helpers.getCurrentPage();
          const hasPreviousPage = helpers.hasPreviousPage();
          const hasNextPage = helpers.hasNextPage();

          const goToFirstPage = () => {
            helpers.goToPage(1);
          };

          return html`
            <div class="pagination">
              <div class="pagination__btn-left">
              ${props.showFirstPageLink
                ? html`
                    <button
                      class=${`btn primary ${currentPage === 1 ? " disabled" : ""}`}
                      onclick=${currentPage === 1 ? undefined : goToFirstPage}
                    >
                      ${labels.pagination.firstPage}
                    </button>
                  `
                : null}
              ${props.showPreviousPageLink
                ? html`
                    <button
                      class=${`btn primary pagination__previous ${!hasPreviousPage ? " disabled" : ""}`}
                      onclick=${currentPage === 1
                        ? undefined
                        : helpers.goToPreviousPage}
                    >
                      ${labels.pagination.previousPage}
                    </button>
                  `
                : null}
              </div>
              <div class="pagination__btn-center">
              ${props.showNumberedPageLinks
                ? pagesToDisplay.map((pageDetail) => {
                    var classes = ["btn", "btn-as-link"];
                    var showEllipsis = pageDetail.jumpAfter;
                    if (pageDetail.current) classes.push("active");
                    if (pageDetail.jumpAfter) classes.push("page-jump-over");
                    var className = classes.join(" ");
                    return html`
                      <button
                        class=${`${className}`}
                        onclick=${() => {
                          !showEllipsis && helpers.goToPage(pageDetail.page);
                        }}
                      >
                        ${showEllipsis ? "..." : pageDetail.page}
                      </button>
                    `;
                  })
                : null}
              </div>
              <div class="pagination__btn-right">
              ${props.showNextPageLink
                ? html`
                    <button
                      class=${`btn primary pagination__next ${!hasNextPage ? " disabled" : ""}`}
                      onclick=${hasNextPage ? helpers.goToNextPage : undefined}
                    >
                      ${labels.pagination.nextPage}
                    </button>
                  `
                : null}
              </div>
            </div>
          `;
        },
      },
    },
  });
};

/**
 * Tagalys recommendations init.
 * @param {Array} [productIds] - Product IDs in cart.
 */
TagalysCustomisations.initRecommendations = function(productIds = []) {
  const breakpoint = isDesktop() ? 'desktop' : 'mobile';
  const cartPage = window.templateData.name === 'cart'
  const placementType = cartPage ? 'page' : `drawer-${breakpoint}`

  const selector = cartPage
    ? `[js-cart-recommendations="${placementType}"]`
    : `[js-cart-recommendations="${placementType}"]`

  const recommendationId = productIds?.length
    ? window.tagalysConfiguration.recommendationsId
    : window.tagalysConfiguration.recommendationsSmartId

  if (!recommendationId) {
    return;
  }

  Tagalys.UIWidgets.Recommendations.init(selector, {
    recommendationId,
    productIds,
    callbacks: {
      onSuccessfulAPICall: (response) => handleSuccessfulAPICall(response, 'predictive-search'),
      afterInitialRender: () => {

        /**
         * Remove class and ID to strip Tagalys styles.
         * - .exclude-tagalys-css-reset doesn't work?
         */
        const tagalysElement = document.querySelector(selector);

        if (tagalysElement) {
          tagalysElement.classList.remove('tagalys-namespace');
          tagalysElement.id = '';
        }

        /**
         * Display recommendation panel/section.
         */
        const recommendationSelector = cartPage ? 'section' : 'panel';
        const recommendationElement = document.querySelector(`[js-cart-recommendations="${recommendationSelector}"]`)

        if (recommendationElement) {
          recommendationElement.classList.add('is-active');
        }
      },
      afterEveryRender: () => {
        if (!cartPage) {
          return;
        }

        const slider = document.querySelector('[js-cart-recommendations="slider"]');

        if (!slider) {
          return;
        }

        slider.resetPages();
      },
    },
    templates: {
      widget: {
        render: function (html, args) {
          if (cartPage) {
            return html`
              <${args.templates.products} />
            `;
          }

          return html`
            <div class="cart-recommendations exclude-tagalys-css-reset">
              <${args.templates.header} />
              <${args.templates.products} />
            </div>
          `;
        },
      },
      header: {
        render: function (html) {
          return html`
            <div
              class="
                cart-recommendations__title
                cart-drawer__collection-title
              "
            >
              ${window.tagalysConfiguration.strings.recommendations.title}
            </div>
          `;
        },
      },
      products: {
        render: function (html, args) {
          const props = args.props;
          const products = filterAvailableProducts(props.helpers.getProducts());

          if (cartPage) {
            return html`
              ${products.map((product) => {
                return html`<${args.templates.product} key=${product.id} product=${product} />`;
              })}
            `;
          }

          return html`
            <div class="cart-recommendations__products-container">
              <slider-component
                class="
                  cart-recommendations__slider
                  slider
                  exclude-tagalys-css-reset
                "
              >
                <ul
                  id="Slider-cart-recommendations-${placementType}"
                  class="cart-recommendations__grid exclude-tagalys-css-reset"
                  role="list"
                >
                  ${products.map((product) => {
                    return html`<${args.templates.product} key=${product.id} product=${product} />`;
                  })}
                </ul>
              </slider-component>
            </div>
          `;
        },
      },
      product: {
        render: (html, args) => renderProduct(html, args, 'predictive-search')
      },
      addToBagIcon: {
        render: (html) => renderIcon(html, 'addToBag'),
      },
      badgeIcon: {
        render: (html) => renderIcon(html, 'badge'),
      },
      loadingIcon: {
        render: (html) => renderIcon(html, 'loading'),
      },
      videoSoundController: {
        render: (html) => renderIcon(html, 'videoSoundController'),
      },
      videoPlayPauseController: {
        render: (html) => renderIcon(html, 'videoPlayPauseController'),
      },
      errorIcon: {
        render: (html) => renderIcon(html, 'error'),
      },
    },
  });
};

/**
 * Initializes the Tagalys customizations for search results and suggestions.
 */
onTagalysReady(function () {
  if (window.templateData.name === 'search') {
    TagalysCustomisations.initSearchResults();
  }

  TagalysCustomisations.initSearchSuggestions();

  /**
   * Recommendations.
   */
  if (!window.tagalysConfiguration.enableRecommendations) {
    return;
  }

  window.cartDrawerOpened = false;

  /**
   * Cart updated event.
   */
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    window.cartDrawerOpened = true;
    queryCartAndLoadRecommendations(true);
  });

  /**
   * Handle cart page.
   */
  if (window.templateData.name === 'cart') {
    queryCartAndLoadRecommendations();
    return;
  }

  /**
   * Cart drawer opened event.
   */
  subscribe(PUB_SUB_EVENTS.cartDrawerOpen, () => {
    if (window.cartDrawerOpened) {
      return;
    }

    window.cartDrawerOpened = true;
    queryCartAndLoadRecommendations();
  });
});
